import { createFileRoute } from '@tanstack/react-router'
import {
  GraduationCap,
  Users,
  ScanSearch,
  Library,
  BarChart3,
  Headset,
} from 'lucide-react'
import { motion } from 'framer-motion'
import {
  Container,
  SectionLabel,
  SectionHeading,
  FeatureCard,
  CheckList,
  CtaBand,
  PrimaryButton,
  SecondaryButton,
  fadeUp,
  ease,
} from '@/components/umbrella/ui'

export const Route = createFileRoute('/solutions/wealth-advisors')({
  component: WealthAdvisorsPage,
})

/** Why this matters to an advisory practice. */
const BUSINESS_CASE = [
  {
    stat: '7 in 10',
    title: 'heirs leave their parents’ advisor',
    body: 'Industry research puts heir attrition after a wealth transfer as high as 70%. A protection program that involves spouses and adult children gives your practice a genuine relationship with the next generation before the transfer happens.',
  },
  {
    stat: '#1',
    title: 'loss category is investment fraud',
    body: 'Fake platforms and impostor "advisors" are now the largest category of reported scam losses. Your clients are being pitched between your review meetings, and the pitch looks like your industry.',
  },
  {
    stat: '80%',
    title: 'less likely to engage',
    body: 'People who know how a scam works before it reaches them are up to 80% less likely to engage with it. Education works ahead of the loss, where account monitoring cannot.',
  },
]

/** What an advisor-branded deployment includes. */
const FEATURES = [
  {
    Icon: GraduationCap,
    title: 'Client education in your name',
    body: 'Interactive scam-defense training offered as a benefit of your practice, built for older adults and covering the fraud patterns aimed at people with assets.',
  },
  {
    Icon: Users,
    title: 'The whole family included',
    body: 'Spouses and adult children learn alongside your client. Every family enrollment is a natural, non-salesy touchpoint with the people who inherit.',
  },
  {
    Icon: ScanSearch,
    title: 'AI scam checker',
    body: 'Clients paste a suspicious message, link, or screenshot and get a clear verdict in seconds, before they reply or click.',
  },
  {
    Icon: Library,
    title: 'Scam library',
    body: 'A searchable, always-current guide to active scams, including the investment and impersonation schemes aimed at your client base.',
  },
  {
    Icon: BarChart3,
    title: 'Engagement reporting',
    body: 'Aggregate reporting shows which clients and families are enrolled and learning, without exposing anyone’s individual activity.',
  },
  {
    Icon: Headset,
    title: 'Expert guidance included',
    body: 'Our team provides the expert guidance when a client is unsure about something. Your practice gets the credit without the support load.',
  },
]

const COMPLIANCE_POINTS = [
  'No account access, account linking, or client credentials, ever',
  'No transaction data and minimal personal information collected',
  'Nothing to build or host: a branded program provisioned from our partner portal',
  'We support your clients directly, so nothing lands on your team',
]

function WealthAdvisorsPage() {
  return (
    <div className="bg-white">
      <section className="relative overflow-hidden border-b border-slate-100">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_60%_at_80%_20%,rgba(37,99,235,0.07),transparent_60%)]" />
        <Container className="grid items-center gap-12 py-20 lg:grid-cols-2 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
          >
            <SectionLabel>For Wealth Advisors &amp; RIAs</SectionLabel>
            <h1 className="mt-4 text-4xl font-semibold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl">
              Protect your clients.{' '}
              <span className="text-blue-600">Keep the next generation.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              Offer clients and their families scam-defense education under your
              practice's brand. It protects the people you advise between
              meetings, and it builds a real relationship with the spouses and
              adult children who decide where the money goes next.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <PrimaryButton label="Talk to our team" href="#contact" />
              <SecondaryButton label="See the platform" href="/platform" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease, delay: 0.1 }}
          >
            <img
              src="/sidebyside.png"
              alt="An advisor-branded learning experience on desktop and mobile"
              className="h-auto w-full"
              width={691}
              height={298}
            />
          </motion.div>
        </Container>
      </section>

      {/* Business case */}
      <Container className="py-20">
        <SectionHeading
          label="Why advisors offer it"
          title="Duty of care that pays for itself"
          blurb="Scam losses and generational attrition are both relationship problems. One program addresses both."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {BUSINESS_CASE.map((c, i) => (
            <motion.div
              key={c.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="rounded-2xl border border-slate-200/80 bg-white p-7"
            >
              <p className="text-4xl font-semibold tracking-tight text-blue-600">{c.stat}</p>
              <h3 className="mt-3 text-lg font-semibold text-slate-900">{c.title}</h3>
              <p className="mt-2.5 text-base leading-relaxed text-slate-600">{c.body}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-8 text-sm leading-relaxed text-slate-500">
          Heir-attrition figures reflect published industry research; loss
          rankings reflect FBI IC3 reporting; awareness impact reflects consumer
          fraud research from the BBB Institute and FINRA Investor Education
          Foundation.
        </p>
      </Container>

      {/* Prevention positioning */}
      <section className="bg-slate-50 py-20">
        <Container>
          <motion.div {...fadeUp} className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
              Monitoring reacts. Education prevents.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              Account-monitoring tools alert someone after money is already in
              motion, and most scam payments never trip an alert because the
              victim authorizes them. ScamDefense works earlier, at the moment a
              client decides whether to trust a call, a message, or an
              opportunity. A client who recognizes the manipulation never makes
              the transfer, and your practice is the reason why.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* What's included */}
      <Container className="py-20">
        <SectionHeading
          label="What's included"
          title="A client benefit, branded to your practice"
          blurb="Everything below is provisioned under your name, with your colors and your contact details where they matter."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(f => (
            <FeatureCard key={f.title} {...f} />
          ))}
        </div>
      </Container>

      {/* Light lift */}
      <section className="bg-slate-50 py-20">
        <Container className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div {...fadeUp}>
            <SectionHeading
              align="left"
              label="Deployment"
              title="Light enough for any compliance review"
              blurb="ScamDefense is education and tools, not account access. That keeps vendor due diligence short and the rollout simple."
            />
          </motion.div>
          <motion.div {...fadeUp} className="rounded-2xl border border-slate-200/80 bg-white p-8">
            <CheckList items={COMPLIANCE_POINTS} />
          </motion.div>
        </Container>
      </section>

      <CtaBand
        title="Bring scam defense to the families you advise."
        blurb="Tell us about your practice and we'll show you a live program under an advisor's branding."
      />
    </div>
  )
}
